/**
 * `/welcome`, the two doors (simplification, 2026-09-24): the first screen a
 * signed-in person sees in a workspace with no Team and nothing connected
 * (`useWelcomeRedirect` sends them here once).
 *
 *   Agent Teams   Connect GitHub. The App install round trip comes back to
 *                 `/desks/new`, where Add a team opens as the sheet.
 *   YAGNI Code    the install line, and the desktop builds.
 *
 * Both lead to the same workspace. "Skip for now" opens the home and is
 * remembered for this workspace on this device.
 */
import { useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import type { AuthSessionResponse } from "@yagni/shared";

import { useCan } from "../hooks/useWorkspaceRole.js";
import { oauthInstallUrl } from "../lib/oauthInstallUrls.js";
import { AUTHENTICATED_DEFAULT_PATH } from "../routes/defaults.js";
import { DownloadCodeDialog } from "../shell/DownloadCodeDialog.js";
import { CLI_INSTALL_COMMANDS, useLatestDmgLinks } from "../shell/desktopDownloads.js";
import { Button, Card } from "../ui/index.js";
import { GitHubInstallOutcome } from "./GitHubInstallOutcome.js";
import { SetupStatusBanner } from "./SetupStatusBanner.js";
import { rememberWelcomeSkip } from "./welcomeSkip.js";
import "./doors.css";

/** Where the GitHub App install returns: the one-screen Team create. */
export const DOORS_GITHUB_RETURN = "/desks/new";

export function DoorsRoute() {
  const navigate = useNavigate();
  const outlet = useOutletContext<{ session?: AuthSessionResponse } | null>();
  const session = outlet?.session ?? null;
  const canConnect = useCan("manage-connections", true);
  const links = useLatestDmgLinks();
  const [dialogOpen, setDialogOpen] = useState(false);

  function skip() {
    if (session) rememberWelcomeSkip(session.workspace.id);
    navigate(AUTHENTICATED_DEFAULT_PATH);
  }

  return (
    <main className="doors" data-testid="welcome-doors">
      <header className="doors-head">
        <span className="doors-wordmark">YAGNI</span>
        <h1 className="doors-title">
          Start with whichever you need first. The other is one click away, always.
        </h1>
        {session && <span className="doors-where">{session.workspace.name}</span>}
      </header>
      <SetupStatusBanner />
      <GitHubInstallOutcome returnPath={DOORS_GITHUB_RETURN} />

      <div className="doors-grid">
        <Card variant="staged" padding="none" className="doors-door" data-testid="door-teams">
          <span className="doors-label">Agent Teams</span>
          <h2 className="doors-door-title">Agents that carry tickets to merged pull requests.</h2>
          <p className="doors-door-body">
            Connect GitHub and pick a repository. Your Team plans each ticket, waits for your
            approval, and opens a pull request for you to merge.
          </p>
          {canConnect ? (
            <a
              className="doors-primary"
              href={oauthInstallUrl("github", DOORS_GITHUB_RETURN)}
              data-testid="door-connect-github"
            >
              <GitHubGlyph />
              Connect GitHub
            </a>
          ) : (
            <p className="doors-door-body" data-testid="door-ask-admin">
              An admin connects GitHub for the workspace. Ask one, and your first Team is one
              screen after that.
            </p>
          )}
          <p className="doors-fact">
            Installs the YAGNI GitHub App on your organization.
            <br />
            Reads code. Writes pull requests. Never pushes to main.
          </p>
        </Card>

        <Card variant="staged" padding="none" className="doors-door" data-testid="door-code">
          <span className="doors-label">YAGNI Code</span>
          <h2 className="doors-door-title">
            The coding agent your developers already know how to use.
          </h2>
          <p className="doors-door-body">
            A drop-in CLI and desktop app on the same rate card, 60% or more under frontier
            rates. Works alone today. Joins a Team whenever you make one.
          </p>
          <code className="doors-install" data-testid="door-install-line">
            {CLI_INSTALL_COMMANDS.posix}
          </code>
          <div className="doors-downloads">
            <a className="doors-quiet" href={links.aarch64} data-testid="door-download-mac">
              Download for Mac
            </a>
            <a className="doors-quiet" href={links.windows} data-testid="door-download-windows">
              Windows
            </a>
            <Button
              variant="secondary"
              className="doors-quiet"
              onClick={() => setDialogOpen(true)}
              data-testid="door-vscode"
            >
              VS Code
            </Button>
          </div>
        </Card>
      </div>

      <button type="button" className="doors-skip" onClick={skip} data-testid="door-skip">
        Skip for now and open the workspace
      </button>
      <DownloadCodeDialog open={dialogOpen} onOpenChange={setDialogOpen} />
    </main>
  );
}

function GitHubGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M12 .5C5.7.5.5 5.7.5 12c0 5.1 3.3 9.4 7.9 10.9.6.1.8-.2.8-.6v-2c-3.2.7-3.9-1.4-3.9-1.4-.5-1.3-1.3-1.7-1.3-1.7-1-.7.1-.7.1-.7 1.2.1 1.8 1.2 1.8 1.2 1 1.8 2.7 1.3 3.4 1 .1-.8.4-1.3.7-1.6-2.6-.3-5.3-1.3-5.3-5.7 0-1.3.5-2.3 1.2-3.1-.1-.3-.5-1.5.1-3.1 0 0 1-.3 3.2 1.2a11 11 0 0 1 5.8 0c2.2-1.5 3.2-1.2 3.2-1.2.6 1.6.2 2.8.1 3.1.8.8 1.2 1.8 1.2 3.1 0 4.4-2.7 5.4-5.3 5.7.4.4.8 1.1.8 2.2v3.2c0 .3.2.7.8.6 4.6-1.5 7.9-5.8 7.9-10.9C23.5 5.7 18.3.5 12 .5z" />
    </svg>
  );
}
